import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, Download } from 'lucide-react';

function ImageDepositCarouselModal({ images, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Debug logs
  useEffect(() => {
    console.log('ImageCarouselModal - isOpen:', isOpen);
    console.log('ImageCarouselModal - images:', images);
    console.log('ImageCarouselModal - images length:', images?.length);
    if (images && images.length > 0) {
      console.log('ImageCarouselModal - First image:', images[0]);
    }
  }, [isOpen, images]);

  // Reset current index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (!isOpen || !images || images.length === 0) {
    console.log('Modal not rendering. isOpen:', isOpen, 'images:', images);
    return null;
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = images[currentIndex].image_url;
    link.download = `comprobante_${images[currentIndex].payment_date || 'payment'}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const currentImage = images[currentIndex];

  console.log('Rendering modal with currentImage:', currentImage);

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl w-full max-w-6xl flex flex-col shadow-2xl" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-white font-montserrat">
              Comprobantes de Pago
            </h3>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-montserrat text-white">
              {currentIndex + 1} / {images.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Image Container */}
        <div className="relative bg-slate-900 flex items-center justify-center overflow-hidden flex-1" style={{ minHeight: '400px' }}>
          <img
            src={currentImage.image_url}
            alt={`Comprobante ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              console.error('Image failed to load:', currentImage.image_url);
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EError al cargar%3C/text%3E%3C/svg%3E';
            }}
          />

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
              >
                <ChevronLeft className="w-6 h-6 text-slate-900" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
              >
                <ChevronRight className="w-6 h-6 text-slate-900" />
              </button>
            </>
          )}
        </div>

        {/* Footer with Image Info */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              {currentImage.payment_date && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm font-montserrat">
                    Fecha de Pago: {new Date(currentImage.payment_date).toLocaleDateString('es-PE')}
                  </span>
                </div>
              )}
              {currentImage.uploaded_at && (
                <div className="text-xs text-slate-500 font-montserrat">
                  Subido: {new Date(currentImage.uploaded_at).toLocaleDateString('es-PE')} {new Date(currentImage.uploaded_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-[#B1336E] text-white rounded-lg font-montserrat hover:bg-[#B1336E]/80 transition-colors shadow-md"
            >
              <Download className="w-4 h-4" />
              Descargar
            </button>
          </div>
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="p-4 border-t border-slate-200 bg-white overflow-x-auto flex-shrink-0" style={{ maxHeight: '140px' }}>
            <div className="flex gap-2 pb-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? 'border-[#B1336E] ring-2 ring-[#B1336E]/30 scale-105 shadow-lg'
                      : 'border-slate-200 hover:border-[#E6B43C] opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.image_url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Thumbnail failed to load:', img.image_url);
                      e.target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageDepositCarouselModal;